/**
 * MarketplaceBrowser - Main marketplace browsing page with filters and search
 * 
 * Accessible to Team Leads and Admins only.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../shared/Card';
import Button from '../shared/Button';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';
import MarketplaceListingCard from './MarketplaceListingCard';
import { SORT_OPTIONS, PRICE_FILTERS } from './MarketplaceCategories';

const MarketplaceBrowser = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, isTeamLead } = useAuth();
  const {
    listings,
    featuredListings,
    listingsLoading,
    error,
    hasMore,
    hasMarketplaceAccess,
    categories,
    initializeMarketplace,
    loadListings,
    loadFeaturedListings,
    searchListings
  } = useMarketplace();

  // Initialize marketplace data on mount
  useEffect(() => {
    initializeMarketplace();
  }, [initializeMarketplace]);

  // Filter state
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedPriceFilter, setSelectedPriceFilter] = useState(searchParams.get('price') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'popular');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  // Update URL when filters change
  useEffect(() => {
    if (!hasMarketplaceAccess) return;
    const params = new URLSearchParams();
    if (selectedCategory !== 'all') params.set('category', selectedCategory);
    if (selectedPriceFilter !== 'all') params.set('price', selectedPriceFilter);
    if (sortBy !== 'popular') params.set('sort', sortBy);
    if (searchQuery) params.set('q', searchQuery);
    setSearchParams(params, { replace: true });
  }, [selectedCategory, selectedPriceFilter, sortBy, searchQuery, setSearchParams, hasMarketplaceAccess]);

  // Load listings when filters change
  useEffect(() => {
    if (!hasMarketplaceAccess) return;
    if (!searchQuery) {
      loadListings({
        category: selectedCategory,
        priceType: selectedPriceFilter
      }, sortBy);
      setSearchResults(null);
    }
  }, [selectedCategory, selectedPriceFilter, sortBy, loadListings, searchQuery, hasMarketplaceAccess]);

  // Handle search
  const handleSearch = useCallback(async () => {
    if (!hasMarketplaceAccess) return;
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    
    setIsSearching(true);
    try {
      const results = await searchListings(searchQuery);
      setSearchResults(results);
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, searchListings, hasMarketplaceAccess]);

  // Debounced search
  useEffect(() => {
    if (!hasMarketplaceAccess) return;
    const timer = setTimeout(() => {
      if (searchQuery) {
        handleSearch();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch, hasMarketplaceAccess]);

  // Check access - MUST be after all hooks
  if (!hasMarketplaceAccess) {
    return (
      <div className="p-4">
        <EmptyState
          icon="🔒"
          title="Access Restricted"
          description="The Script Marketplace is available to Team Leads and Admins only. Please contact your administrator if you need access."
          action={
            <Button variant="primary" onClick={() => navigate('/')}>
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  // Load more listings
  const handleLoadMore = () => {
    loadListings({
      category: selectedCategory,
      priceType: selectedPriceFilter
    }, sortBy, 20, true);
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedCategory('all');
    setSelectedPriceFilter('all');
    setSortBy('popular');
    setSearchQuery('');
  };

  const displayListings = searchResults || listings;
  const hasActiveFilters = selectedCategory !== 'all' || selectedPriceFilter !== 'all' || searchQuery;

  return (
    <div className="w-full">
      {/* Search bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search scripts by title, description, or tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            🔍
          </span>
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card padding="sm" className="mb-6">
        <div className="flex flex-wrap items-center gap-4">
          {/* Industry filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Industry:</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Industries</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.icon} {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Price filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Price:</label>
            <select
              value={selectedPriceFilter}
              onChange={(e) => setSelectedPriceFilter(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              {PRICE_FILTERS.map(filter => (
                <option key={filter.id} value={filter.id}>
                  {filter.name}
                </option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Sort:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              {SORT_OPTIONS.map(option => (
                <option key={option.id} value={option.id}>
                  {option.icon} {option.name}
                </option>
              ))}
            </select>
          </div>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          )}
        </div>
      </Card>

      {/* Featured section (only show when not searching/filtering) */}
      {!hasActiveFilters && featuredListings.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>⭐</span> Featured Scripts
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featuredListings.slice(0, 3).map(listing => (
              <MarketplaceListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        </div>
      )}

      {/* Main listings */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          {searchQuery ? `Search Results for "${searchQuery}"` : 'All Scripts'}
          {displayListings.length > 0 && (
            <span className="text-gray-500 font-normal ml-2">
              ({displayListings.length} {displayListings.length === 1 ? 'script' : 'scripts'})
            </span>
          )}
        </h2>

        {/* Loading state */}
        {(listingsLoading || isSearching) && displayListings.length === 0 && (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="large" message="Loading scripts..." />
          </div>
        )}

        {/* Error state */}
        {error && !listingsLoading && (
          <EmptyState
            icon="⚠️"
            title="Couldn't load scripts"
            description={error}
            action={
              <Button variant="primary" onClick={() => loadListings()}>
                Try Again
              </Button>
            }
          />
        )}

        {/* Empty state */}
        {!listingsLoading && !isSearching && !error && displayListings.length === 0 && (
          <EmptyState
            icon="📋"
            title={searchQuery ? 'No scripts found' : 'No scripts available'}
            description={
              searchQuery
                ? `No scripts match "${searchQuery}". Try a different search term.`
                : 'Be the first to list a script on the marketplace!'
            }
            action={
              searchQuery ? (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Search
                </Button>
              ) : (
                <Button variant="primary" onClick={() => navigate('/marketplace/sell')}>
                  List Your First Script
                </Button>
              )
            }
          />
        )}

        {/* Listings grid */}
        {displayListings.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {displayListings.map(listing => (
                <MarketplaceListingCard key={listing.id} listing={listing} />
              ))}
            </div>

            {/* Load more button */}
            {!searchResults && hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  disabled={listingsLoading}
                >
                  {listingsLoading ? 'Loading...' : 'Load More Scripts'}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
};

export default MarketplaceBrowser;
